/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useEffect, useState } from 'react';
import { AgenciesService } from '@services/requests';
import type { SeatUsageSummary } from '@services/requests/housien-services/AgenciesService.subscription';

export interface UseSeatUsageResult {
  data: SeatUsageSummary | null;
  isLoading: boolean;
  /** Reads the usage again: a full quota can open mid-period (RN-10). */
  reload: () => void;
}

/**
 * Fetches the agency's metered usage for the current period.
 *
 * A failure resolves to `null` rather than to an error state: the usage bars
 * are one block of the plan tab, and an agency whose usage endpoint is having
 * a bad minute should still see its price, its invoices and its cancel
 * button. The block simply does not render.
 *
 * @param agencyId - The agency identifier.
 * @returns The usage summary, or null while loading or after a failure.
 */
export function useSeatUsage(agencyId: string): UseSeatUsageResult {
  const [data, setData] = useState<SeatUsageSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    if (!agencyId) return undefined;
    setIsLoading(true);

    AgenciesService.getAgencySeatUsage(agencyId)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [agencyId, tick]);

  return { data, isLoading, reload };
}
